import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-community/core'
import { StatisticalAnalysisDatasource } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { Nullable } from '@/shared/types/app.types'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisIncidentTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { StatisticalAnalysisIncidentsStatisticsDossierBaseRequest, StatisticalAnalysisIncidentsStatisticsDossierDetailRequest, StatisticalAnalysisIncidentsStatisticsDossierDetailViewTable, StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent, StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.dto'
import { StatisticalAnalysisIncidentsDossierExternalInfo } from '@/modules/statistical-analysis-module/views/incidents-dossier/incidents-dossier.model'
import { StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { incidentsStatisticsDossierService } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.service'
import { ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { forEach, concat } from 'lodash'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'

export class StatisticalAnalysisHandlerDatasourceIncidentsDossier extends StatisticalAnalysisDatasource<StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
    }

    public getGridColumns(): ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef [] {
        return concat([], [{ type: AgGridColumnType.CUSTOM_CHECKBOX } as ColDef], this._filterGroupedGridColumns(), this._getStatsColumns())
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisIncidentsStatisticsDossierDetailViewTableContent>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisIncidentsDossierExternalInfo
        const baseRequest: StatisticalAnalysisIncidentsStatisticsDossierBaseRequest = incidentsStatisticsDossierService.getBaseRequest(sidebarFilter)
        const detailRequest: StatisticalAnalysisIncidentsStatisticsDossierDetailRequest = {
            groupByFields: (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName),
            infoDetail: sidebarFilter.temporalFilter.id,
            infoType: sidebarFilter.typeTimeFilter.id
        }
        const entityTypes = (sidebarFilter?.typeSearch as StatisticalAnalysisSidebarItemCombo[]).map((item: StatisticalAnalysisSidebarItemCombo) => item.id) as StatiscalAnalysisTypeCases[]
        const state = sidebarFilter.trackingGeneralSelected?.key as StatisticalAnalysisIncidentTypes

        const response: StatisticalAnalysisIncidentsStatisticsDossierDetailViewTable = await incidentsStatisticsDossierService.getDetailViewTable(filterModel, params, entityTypes, state, baseRequest, detailRequest, scrollId)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response.scrollPage.scrollId)),
                size: response.scrollPage.size,
                totalElements: null
            }
        }
    }

    private _getStatsColumns(): ExportedColGroupDef[] {
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.statistics'),
            children: [
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc('statisticalAnalysis.grids.incidents'),
                    field: 'stats.totalIncidents',
                    exportField: 'stats.totalIncidents',
                    exportType: AgGridExportType.NUMBER,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.percentage'),
                    field: 'stats.percent',
                    exportField: 'stats.percent',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.maximumTime'),
                    field: 'stats.maxTime',
                    exportField: 'stats.maxTime',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.minimumTime'),
                    field: 'stats.minTime',
                    exportField: 'stats.minTime',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.average'),
                    field: 'stats.mean',
                    exportField: 'stats.mean',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.median'),
                    field: 'stats.median',
                    exportField: 'stats.median',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                },
                {
                    type: AgGridColumnType.DECIMAL,
                    headerName: i18n.tc('statisticalAnalysis.grids.standardDeviation'),
                    field: 'stats.deviation',
                    exportField: 'stats.deviation',
                    exportType: AgGridExportType.DOUBLE,
                    minWidth: 100,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                }]
        }]
    }

    private _getGroupingColumns(): ExportedColDef [] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.incidentOrigin'),
                field: 'incidentOrigin.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.incidentType'),
                field: 'incidentType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.severity'),
                field: 'incidentSeverity.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.affectedEntity'),
                field: 'coreEntity.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.openingResponsible'),
                field: 'openedBy.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.closingResponsible'),
                field: 'closedBy.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('statisticalAnalysis.dossiers.incidents.status.open'),
                field: 'isOpen.literal',
                exportType: AgGridExportType.BOOLEAN,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.casePhase'),
                field: 'phase.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }]
    }

    private _filterGroupedGridColumns(): ColGroupDef[] {
        const colDefs: ColDef[] = []
        const groupedColumns = this._getGroupingColumns()
        forEach(this.statisticalAnalysisFilters?.grouping, (item: StatisticalAnalysisGroupingResponseField) => {
            switch (item.fieldName as StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum) {
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.INCIDENT_ORIGIN:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'incidentOrigin.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.INCIDENT_SEVERITY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'incidentSeverity.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.INCIDENT_TYPE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'incidentType.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.CORE_ENTITY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'coreEntity.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.OPENED_BY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'openedBy.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.CLOSED_BY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'closedBy.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.PHASE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'phase.literal') as ColDef)
                break
            case StatisticalAnalysisIncidentsStatisticsDossierGroupingColumnEnum.IS_OPEN:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'isOpen.literal') as ColDef)
                break
            default: break
            }
        })
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.groupings'),
            children: colDefs
        }]
    }
}
